import axios from "axios";
import {
  convertBase64,
  decrypt,
  decryptAES,
  encrypt,
  encryptAES,
  generateIV,
  generateKey,
  parseBase64,
} from "utils/encryption";

export const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

export const eCommerceServiceInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/ecommerce-service`,
});

export const paymentServiceInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/payment-service`,
});
export const paymentServiceInstanceUser = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/payment-service`,
});

export const commonServiceInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/common-service/api/v1`,
});
export const notificationServiceInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/notification-service/api/v1`,
});
export const socialSevrice = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/social-service/api/v1`,
});

axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    }
    // config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

const refreshLogin = async (refresh_token) => {
  const {
    data: { value: data },
  } = await commonServiceInstance.post(`/auth/web-refresh-login`, {
    refresh_token,
  });
  return data;
};

const requestSuccessHandler = (config) => {
  const accessToken = localStorage.getItem("userToken");
  if (config.headers && accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

const responseErrorHandler = async (
  originalRequest,
  instance //InternalAxiosRequestConfig,
) => {
  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken) {
    return Promise.reject(originalRequest);
  }
  try {
    if (originalRequest?.response?.status === 401 && !originalRequest._retry) {
      const data = await refreshLogin(refreshToken);
      localStorage.setItem("userToken", data.access_token);
      localStorage.setItem("refreshToken", data.refresh_token);
      originalRequest._retry = true;

      return instance(originalRequest.config);
    }
  } catch (error) {
    localStorage.removeItem("userToken");
    localStorage.removeItem("refreshToken");
  }
  return Promise.reject(originalRequest);
};

commonServiceInstance.interceptors.request.use(
  requestSuccessHandler,
  (error) => error
);
commonServiceInstance.interceptors.response.use(
  (response) => response,
  (originalRequest) =>
    responseErrorHandler(originalRequest, commonServiceInstance)
);
paymentServiceInstanceUser.interceptors.request.use(
  requestSuccessHandler,
  (error) => error
);
paymentServiceInstanceUser.interceptors.response.use(
  (response) => response,
  (originalRequest) =>
    responseErrorHandler(originalRequest, paymentServiceInstanceUser)
);
notificationServiceInstance.interceptors.request.use(
  requestSuccessHandler,
  (error) => error
);
notificationServiceInstance.interceptors.response.use(
  (response) => response,
  (originalRequest) =>
    responseErrorHandler(originalRequest, paymentServiceInstanceUser)
);

export const orderCityPay = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(`/api/external/citypay`, payload);
  return data;
};

export const orderCryptoPay = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(`/api/external/crypto/pay`, payload);
  return data;
};
export const orderCryptoPay2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(`/api/external/crypto/pay/v2`, {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const orderCityPayStatus = async (params) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.get(`/api/external/citypay?` + params);
  return data;
};

export const bogTransaction = async (payload) => {
  const { data } = await paymentServiceInstance.post(
    "/api/external/bog/transaction/pay",
    payload
  );

  return data;
};
export const tbcTransaction = async (payload) => {
  const { data } = await paymentServiceInstance.post(
    "/api/external/tbc/transaction/pay",
    payload
  );

  return decrypt(data.encryptedData);
};

export const credoTransaction = async (payload) => {
  const { data } = await paymentServiceInstance.post(
    "/api/external/credo/transaction/pay",
    payload
  );

  return decrypt(data.encryptedData);
};

export const standardTransaction = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/transaction/external/pay", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getSystemStatus = async () => {
  const resp = await axiosClient.get("/api/system/status");

  return resp;
};

export const getToken = async () => {
  const payload = {
    password: "someBank",
    username: "someBank",
  };

  const resp = await axiosClient.post(`/api/external/authenticate`, payload);
  return resp;
};

export const getUser = async (userId) => {
  const resp = axiosClient.get(`/api/user/info/id/${userId}`);

  return resp;
};

export const getSinglePayLimit = async () => {
  const { data } = await axiosClient.get(`/api/single-payment/limit`);

  return data;
};

export const makePayzeTransaction = async (payload) => {
  const { data } = await axiosClient.post(
    `/api/payze/transaction/pay`,
    payload
  );
  return data;
};

export const makeApplePayTransaction = async (payload) => {
  const { data } = await axiosClient.post(
    `/api/payze/transaction/pay`,
    payload
  );
  return data;
};

export const makeBogTransaction = async (payload) => {
  const { data } = await axiosClient.post(`/api/bog/transaction/pay`, payload);
  return data;
};

export const startApplePayment = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/external/bog/transaction/pay/applePay",
    payload
  );
  return data;
};
export const startApplePayment2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "/api/external/bog/transaction/pay/applePay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptApplePay = async (payload, params) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post("/api/gc/transaction/accept", payload, {
    params,
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
  });
  return data;
};
export const acceptApplePay2 = async (payload, params) => {
  const body = {
    requestBody: payload,
    ...params,
  };
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/bog/transaction/accept/applePay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(body, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    },
    {
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptApplePayTbc = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/tbc-ma/transaction/apple/register",
    payload
  );
  return data;
};
export const acceptApplePayTbc2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/apple/register",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};
export const acceptApplePayCredo = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/ma/transaction/apple/register",
    payload
  );
  return data;
};
export const acceptApplePayCredo2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/apple/register",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptApplePayCredoTest = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/ma/transaction/apple/register",
    payload
  );
  return data;
};

export const acceptApplePayMollie = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/ma/transaction/apple/register",
    payload
  );
  return data;
};

export const makeEmoneyTransaction = async (payload) => {
  const { data } = await axiosClient.post(
    `/api/card/transaction/make`,
    payload
  );
  return data;
};

export const getPaymentProvider = async () => {
  const { data } = await axiosClient.get("/api/paymentProvider");

  return data;
};

export const addReview = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post("/api/v1/review/encrypted", payload);

  return decrypt(data.encryptedData);
};

export const sendPartnershipRequest = async (data) => {
  const res = await paymentServiceInstance.post("/api/v1/info/new", data);
  return res;
};

export const postTransactionAction = async (integratorOrderId) => {
  try {
    const IV = generateIV();
    const secretkey = generateKey();
    const ivBase64 = convertBase64(IV);
    const secretKeyBase64 = convertBase64(secretkey);
    const base64 = `${secretKeyBase64}.${ivBase64}`;
    const payload = {
      integratorOrderId,
    };
    // const encryptedData = encrypt(
    //   { integratorOrderId },
    //   { shouldEncode: true }
    // );
    const {
      data: {
        value: { encryptedData, encryptedKeys },
      },
    } = await eCommerceServiceInstance.get(
      `/api/integrator/order/post-transaction/external/check?identifier=${encodeURI(
        process.env.REACT_APP_INTEGRATOR_ID
      )}&encryptedData=${encryptAES(payload, secretkey, IV, {
        shouldEncode: true,
      })}&encryptedKeys=${encrypt(base64, {
        stringify: false,
        shouldEncode: true,
      })}`
    );
    const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
    const [base64SecretKey, base64IV] = decryptedKeys.split(".");
    const secretKey = parseBase64(base64SecretKey);
    const iv = parseBase64(base64IV);
    return decryptAES(encryptedData, secretKey, iv);
  } catch (e) {
    console.error(e);
  }
};
export const checkOrderStatus = async (integratorOrderId, integratorId) => {
  // const encryptedData = encrypt(
  //   { integratorOrderId, integratorId },
  //   { shouldEncode: true }
  // );
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const encryptedDataOne = {
    integratorId: integratorId,
    integratorOrderId: integratorOrderId,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await eCommerceServiceInstance.post(
    `/api/integrator/order/request-order-check/external`,
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(encryptedDataOne, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
      aes: true,
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  const decryptedData = decryptAES(encryptedData, secretKey, iv);

  return decryptedData;
};

export const getPendingStatus = async (transactionId) => {
  const encryptedDataOne = encrypt(
    { id: transactionId },
    { shouldEncode: true }
  );
  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `/api/external/bog/transaction/check/appleGoogle?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedDataOne}`
  );

  return data;
};

export const tipWithCard = async (
  transactionId,
  amount,
  percent,
  senderCommission
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
  });

  const data = paymentServiceInstance.post("api/tips/external/pay", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptedDataOne,
  });

  return data;
};
export const tipWithCardTbc = async (
  transactionId,
  amount,
  percent,
  senderCommission
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
  });

  const data = paymentServiceInstance.post("api/tips/external/pay/tbc", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptedDataOne,
  });

  return data;
};
export const tipWithCardCredo = async (
  transactionId,
  amount,
  percent,
  senderCommission
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
  });

  const { data } = await paymentServiceInstance.post(
    "api/tips/external/pay/credo",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};

export const tipCard = async (payload) => {
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/tips/external/pay", payload);
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const tipWithGooglePay = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...(currency == "EUR" && { convertedAmount: convertedAmount }),
  });

  const { data } = await paymentServiceInstance.post(
    "api/tips/external/pay/googlePay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};
export const tipWithGooglePayTbc = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...(currency == "EUR" && { convertedAmount: convertedAmount }),
  });

  const { data } = await paymentServiceInstance.post(
    "api/tips/external/pay/tbc/google-pay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};
export const tipWithGooglePayCredo = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...(currency == "EUR" && { convertedAmount: convertedAmount }),
  });

  const { data } = await paymentServiceInstance.post(
    "api/tips/external/pay/credo/google-pay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};

export const tipGoogle = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency,
  acquiringType
) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    id: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...((currency == "EUR" || currency == "USD") && {
      convertedAmount: convertedAmount,
    }),
    acquiringType: acquiringType,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/tips/external/pay/google", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const tipCityPay = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency,
  acquiringType
) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    id: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...((currency == "EUR" || currency == "USD") && {
      convertedAmount: convertedAmount,
    }),
    acquiringType: acquiringType,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/tips/external/pay", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const tipWithApplePay = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency
) => {
  const encryptedDataOne = encrypt({
    id: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...(currency == "EUR" && { convertedAmount: convertedAmount }),
  });

  const { data } = await paymentServiceInstance.post(
    `api/tips/external/pay/applePay`,
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};
export const tipWithApplePayTbc = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...(currency == "EUR" && { convertedAmount: convertedAmount }),
  });

  const { data } = await paymentServiceInstance.post(
    `/api/tips/external/pay/tbc/apple-pay`,
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};
export const tipWithApplePayCredo = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...(currency == "EUR" && { convertedAmount: convertedAmount }),
  });

  const { data } = await paymentServiceInstance.post(
    `/api/tips/external/pay/credo/apple-pay`,
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};

export const tipApple = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  convertedAmount,
  currency,
  acquiringType
) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    id: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    ...((currency == "EUR" || currency == "USD") && {
      convertedAmount: convertedAmount,
    }),
    acquiringType: acquiringType,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/tips/external/pay/apple", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getTransactionDetails = async (transactionId) => {
  const encryptedDataOne = encrypt(
    {
      id: transactionId,
    },
    { shouldEncode: true }
  );

  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `api/external/bog/transaction/details?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedDataOne}`
  );

  return decrypt(data.encryptedData);
};
export const getTransactionDetailsTbc = async (transactionId) => {
  const encryptedDataOne = encrypt(
    {
      id: transactionId,
    },
    { shouldEncode: true }
  );

  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `api/tbc/transaction/external/details?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedDataOne}`
  );

  return decrypt(data.encryptedData);
};

export const getTransactionDetailsCredo = async (transactionId) => {
  const encryptedDataOne = encrypt(
    {
      id: transactionId,
    },
    { shouldEncode: true }
  );

  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `api/credo/transaction/external/details?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedDataOne}`
  );

  return decrypt(data.encryptedData);
};

export const getTransDetails = async (data, secretkeys) => {
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.get(
    `/api/transaction/external/invoice?identifier=${process.env.REACT_APP_INTEGRATOR_ID}&encryptedData=${data}&encryptedKeys=${secretkeys}`
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getCityPayTransDetails = async (transactionId) => {
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.get(
    `/api/external/crypto/invoice?identifier=${process.env.REACT_APP_INTEGRATOR_ID}&acquiringId=${transactionId}&paymentProviderEnum=CITYPAY`
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getThreeDsStatus = async (token) => {
  const encryptedData = encrypt(
    {
      token: token,
    },
    { shouldEncode: true }
  );

  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `api/external/bog/transaction/history/google?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedData}`
  );

  return decrypt(data.encryptedData);
};

export const startUfcApplePayment = async (payload) => {
  const encryptedDataOne = encrypt(payload);

  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/pay/applePay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return decrypt(data.encryptedData);
};

export const startUfcApplePayment2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/pay/applePay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptNuveiApplePayment = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/nuvei/transaction/apple/register/external",
    {
      aes: true,
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const startNuveiApplePayment = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/nuvei/transaction/apple/pay/external",
    {
      aes: true,
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getNuveiAppleSession = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/nuvei/transaction/apple/merchant/validate/external",
    {
      aes: true,
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const startTbcApplePayment = async (payload) => {
  const encryptedDataOne = encrypt(payload);

  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/pay/applePay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return decrypt(data.encryptedData);
};
export const startTbcApplePayment2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/pay/applePay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const startMollieApplePayment = async (payload) => {
  const encryptedDataOne = encrypt(payload);

  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/pay/applePay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return decrypt(data.encryptedData);
};

export const startUfcGooglePayment = async (payload) => {
  const encryptedDataOne = encrypt(payload);

  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/pay/googlePay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return decrypt(data.encryptedData);
};
export const startUfcGooglePayment2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/pay/googlePay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};
export const startNuveiGooglePayment = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/nuvei/transaction/google/pay/external",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};
export const startTbcGooglePayment = async (payload) => {
  const encryptedDataOne = encrypt(payload);

  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/pay/googlePay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return decrypt(data.encryptedData);
};

export const startTbcGooglePayment2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/pay/googlePay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptUfcGooglePayPayment = async (payload, params) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    `api/ma/transaction/google/register/${encodeURI(params)}`,
    payload
  );
  return data;
};
export const acceptUfcGooglePayPayment2 = async (payload, params) => {
  const body = {
    request: {
      ...payload,
    },
    id: params,
  };
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/google/register",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(body, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptNuveiGooglePayPayment = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/nuvei/transaction/google/register/external",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const acceptUfcGooglePayPaymentTwo = async (payload, params) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    `api/ma/transaction/google/register/${encodeURI(params)}`,
    payload
  );
  return data;
};

export const acceptTbcGooglePayPayment = async (payload, params) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    `api/tbc-ma/transaction/google/register/${encodeURI(params)}`,
    payload
  );
  return data;
};
export const acceptTbcGooglePayPayment2 = async (payload, params) => {
  const body = {
    request: {
      ...payload,
    },
    id: params,
  };
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/tbc-ma/transaction/google/register",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(body, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getCommisionForTips = async (userId, userType, transactionId) => {
  const encryptedData = encrypt(
    {
      userId: userId,
      userType: userType,
      transactionId: transactionId,
    },
    { shouldEncode: true }
  );
  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `api/tips/external/config?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedData}`
  );

  return decrypt(data.encryptedData);
};

export const oBRequest = async (payload) => {
  const encryptedDataOne = encrypt(payload);

  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "api/v1/open-banking/transaction/external/pay",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return data;
};

export const oBRequest2 = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/v1/open-banking/transaction/external/pay/v2",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const obStatusCheck = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/v1/open-banking/transaction/external/finalise",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const consentCheck = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.put(
    "api/v1/ecommerce/consent/finalise/encrypted",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const tipOBRequest = async (
  transactionId,
  amount,
  percent,
  senderCommission,
  openBankingProviderId
) => {
  const encryptedDataOne = encrypt({
    transactionId: transactionId,
    ...(amount && { amount: amount }),
    ...(percent && { percent: percent }),
    ...(senderCommission && { senderCommission: senderCommission }),
    openBankingProviderId: openBankingProviderId,
  });
  const {
    data: { value: data },
  } = await paymentServiceInstance.post("api/tips/external/pay/ob", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptedDataOne,
  });

  return data;
};

export const obStatusCheckRepeat = async (id) => {
  const encryptedDataOne = encrypt({
    id: id,
  });
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/v1/open-banking/transaction/external/status",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptedDataOne,
    }
  );

  return decrypt(data.encryptedData);
};

export const getTransactionDetailsOB = async (transactionId) => {
  const encryptedDataOne = encrypt(
    {
      id: transactionId,
    },
    { shouldEncode: true }
  );

  const {
    data: { value: data },
  } = await paymentServiceInstance.get(
    `api/v1/open-banking/transaction/external/details?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptedDataOne}`
  );

  return decrypt(data.encryptedData);
};

export const getOBProviders = async () => {
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance(
    `api/v1/open-banking/provider/external/get-providers?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}`
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const credoInstallment = async (payload) => {
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/credo/installment",
    payload
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const resetFailOrder = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const data = await eCommerceServiceInstance.post(
    "/api/integrator/order/fail/external",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
      aes: true,
    }
  );

  return data;
};

export const convertCurrency = async (
  {
    amount,
    senderCommission = 0,
    senderCommisionGoogle = 0,
    senderCommisionApple = 0,
    senderCommisionStandard = 0,
    senderCommisionCrypto = 0,
    senderCommisionOB = 0,
  },
  currency,
  acquiringCurrency
) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    amounts: [
      amount,
      senderCommission,
      senderCommisionGoogle,
      senderCommisionApple,
      senderCommisionStandard,
      senderCommisionCrypto,
      senderCommisionOB,
    ],
    from: currency,
    to: acquiringCurrency,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/v1/convert/external", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const convertCurrencyTest = async (
  {
    amount,
    senderCommission = 0,
    senderCommisionGoogle = 0,
    senderCommisionApple = 0,
  },
  currency,
  acquiringCurrency
) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    amounts: [
      amount,
      senderCommission,
      senderCommisionGoogle,
      senderCommisionApple,
    ],
    from: currency,
    to: acquiringCurrency,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/v1/convert/external", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const credoInsallmentInvoice = async (id) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    id: id,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.get(
    `/api/external/credo/installment/invoice?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptAES(payload, secretkey, IV, {
      shouldEncode: true,
    })}&encryptedKeys=${encrypt(base64, {
      stringify: false,
      shouldEncode: true,
    })}`
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const threeDsCredoFinish = async (id) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    id: id,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post("/api/external/ma/transaction/finish", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const threeDsTbcFinish = async (id) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    id: id,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "/api/external/tbc-ma/transaction/finish",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const credoInstStatusUpdate = async (orderCode) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    orderCode: orderCode,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "/api/external/credo/installment/update",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getCommisionAmount = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "/api/v1/commission/calculate/external",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const getCommisionAmountsEcomm = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "/api/v1/commission/calculate/ecommerce/external",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const sendNumber = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const data = await socialSevrice.post("/awarded-gift/external/sms", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
  });

  return data;
};

export const getPromoCode = async (
  vendroName,
  acquiringId,
  paymentProvider
) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await socialSevrice.get(
    `/awarded-gift/external/voucher?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&vendorName=${vendroName}&acquiringId=${acquiringId}&paymentProvider=${paymentProvider}`
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  console.log(decryptAES(encryptedData, secretKey, iv), "voucher code");
  return decryptAES(encryptedData, secretKey, iv);
};

export const cityPayStatusCheck = async (acquringId) => {
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.get(
    `api/external/crypto/details?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&acquiringId=${acquringId}&paymentProviderEnum=CITYPAY`
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const rsCreate = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await eCommerceServiceInstance.post("/api/integrator/order/rs/create", {
    identifier: process.env.REACT_APP_INTEGRATOR_ID,
    encryptedData: encryptAES(payload, secretkey, IV),
    encryptedKeys: encrypt(base64, { stringify: false }),
    aes: true,
  });
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  console.log(decryptAES(encryptedData, secretKey, iv), "Rs data");
  return decryptAES(encryptedData, secretKey, iv);
};

export const getExternal = async (provider, companyId, branchId, itemId) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;
  const payload = {
    provider,
    companyId,
    branchId,
    itemId,
  };
  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.get(
    `/api/external/item/qr/encrypted?identifier=${encodeURI(
      process.env.REACT_APP_INTEGRATOR_ID
    )}&encryptedData=${encryptAES(payload, secretkey, IV, {
      shouldEncode: true,
    })}&encryptedKeys=${encrypt(base64, {
      stringify: false,
      shouldEncode: true,
    })}`
  );
  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const login = async (countryCode, mobile, token, version) => {
  const {
    data: { value: data },
  } = await commonServiceInstance.get(
    `/auth/exists?countryCode=${countryCode}&mobile=${mobile}&recaptchaToken=${token}&recaptchaType=${version}`
  );

  return data;
};

export const verify = async (countryCode, mobileNumber, userType, otpCode) => {
  const {
    data: {
      value: { access_token, refresh_token },
    },
  } = await commonServiceInstance.post(`/auth/web-login`, {
    countryCode,
    mobileNumber,
    userType,
    otpCode,
  });

  return { access_token, refresh_token };
};

export const resendCode = async (state) => {
  const {
    data: { value: data },
  } = await commonServiceInstance.post("/auth/web-resend-otp", {
    state,
  });
  return data;
};

export const verifySms = async (countryCode, phone, code) => {
  const {
    data: { value: data },
  } = await commonServiceInstance.post(`/auth/verify-sms`, {
    countryCode,
    phone,
    code,
  });

  return data;
};
export const register = async (formData, userSMSId) => {
  const {
    data: {
      value: { access_token, refresh_token },
    },
  } = await commonServiceInstance.post(`/auth/web-registration`, {
    ...formData,
    userType: "INDIVIDUAL",
    userSMSId,
  });

  return { access_token, refresh_token };
};

export const generateQR = async (formData, token) => {
  const {
    data: { value: data },
  } = await commonServiceInstance.post(`/auth/send-web-registration-otp`, {
    name: formData.name,
    iban: formData.iban,
    amount: formData.amount,
    phoneNumberDetails: {
      phoneNumber: formData.number,
      countryCode: "995",
    },
    userType: "INDIVIDUAL",
    reCaptchaToken: token || null,
  });

  return data;
};

export const sendOtpForUrl = async (number, otp) => {
  const {
    data: { value: data },
  } = await commonServiceInstance.post(`/auth/verify-web-registration-otp`, {
    phoneNumberDetails: {
      phoneNumber: number,
      countryCode: "995",
    },
    otp,
  });

  return data;
};

export const setAmountForDefault = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstanceUser.post(
    "/api/v1/amount-for-default",
    payload
  );
  return data;
};

export const setCommissonType = async (commissionType, userType) => {
  const userTypeMap = {
    INDIVIDUAL: "user-account",
    BUSINESS: "branch-account",
  };
  const {
    data: { value: data },
  } = await paymentServiceInstanceUser.put(
    `/api/v1/${userTypeMap[userType]}/commissionType`,
    null,
    {
      params: { commissionType },
    }
  );

  return data;
};
export const testStart = async (provider = "APPLE", currency, amount) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/payment/start",
    {
      amount: Number(amount),
      currency: currency,
      receiverId: "20398676-6669-47e7-a796-da4fcc0d3f39",
      paymentProvider: provider,
    },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tkn")}`,
      },
    }
  );

  return data;
};

export const testFinish = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/payment/finish/apple-pay",
    { ...payload },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tkn")}`,
      },
    }
  );

  return data;
};

export const tokenTest = async () => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post("/api/auth/token", {
    clientId: "084c5b46-d44a-455e-b1ad-fe1163dc6eea",
    clientSecret: "978d0053-a1a6-4d34-a44c-bcd8f5835473",
    grantType: "client_credentials",
  });

  const { accessToken } = data;
  localStorage.setItem("tkn", accessToken);

  return data;
};

export const testFinishGoogle = async (payload) => {
  const {
    data: { value: data },
  } = await paymentServiceInstance.post(
    "/api/payment/finish/google-pay",
    { ...payload },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tkn")}`,
      },
    }
  );

  return data;
};

export const getExcelTempalte = async (id) => {
  const {
    data: { value: binaryFile },
  } = await paymentServiceInstanceUser.get(
    `api/bulk-sms/template/download/${id}`
  );

  return binaryFile;
};

export const validataExcelTemplate = async (id, file) => {
  const formData = new FormData();
  formData.append("templateId", id);
  formData.append("file", file);

  const {
    data: { value: data },
  } = await paymentServiceInstanceUser.post(
    `/api/bulk-sms/validate`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return data;
};

export const sendBulkSms = async (
  templateId,
  name,
  commissionType,
  validUntil,
  file
) => {
  const formData = new FormData();
  formData.append("templateId", templateId);
  formData.append("name", name);
  formData.append("commissionType", commissionType);
  formData.append("validUntil", validUntil);
  formData.append("file", file);

  const {
    data: { value: data },
  } = await paymentServiceInstanceUser.post(`/api/bulk-sms/send`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return data;
};

export const deactivateBulkSms = async (userId) => {
  const {
    data: { value: data },
  } = await paymentServiceInstanceUser.delete(`api/bulk-sms/process/${userId}`);

  return data;
};

export const reminderBulkSms = async (userId) => {
  const {
    data: { value: data },
  } = await paymentServiceInstanceUser.post(
    `api/bulk-sms/process/reminder/${userId}`
  );

  return data;
};

export const exportBulkSms = async (id) => {
  const { data } = await paymentServiceInstanceUser.get(
    `/api/bulk-sms/process/${id}/export`,
    { responseType: "blob" }
  );

  console.log(data, "xlsllslsl");

  return data;
};
export const startUfcApplePayment2test = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/pay/applePay/v2/test",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};

export const startUfcGooglePayment2test = async (payload) => {
  const IV = generateIV();
  const secretkey = generateKey();
  const ivBase64 = convertBase64(IV);
  const secretKeyBase64 = convertBase64(secretkey);
  const base64 = `${secretKeyBase64}.${ivBase64}`;

  const {
    data: {
      value: { encryptedData, encryptedKeys },
    },
  } = await paymentServiceInstance.post(
    "api/external/ma/transaction/pay/googlePay/v2/test",
    {
      identifier: process.env.REACT_APP_INTEGRATOR_ID,
      encryptedData: encryptAES(payload, secretkey, IV),
      encryptedKeys: encrypt(base64, { stringify: false }),
    }
  );

  const decryptedKeys = decrypt(encryptedKeys, { shouldParse: false });
  const [base64SecretKey, base64IV] = decryptedKeys.split(".");
  const secretKey = parseBase64(base64SecretKey);
  const iv = parseBase64(base64IV);
  return decryptAES(encryptedData, secretKey, iv);
};
